
import React from 'react';
import { ContentBlock, ContentBlockType, CalloutVariant, ListType } from '../../types/content';
import CodeBlock from '../common/CodeBlock';
import MermaidDiagram from './MermaidDiagram';
import ComponentDiagram from './ComponentDiagram';
import { AlertCircle, CheckCircle, CircleHelp, Info, AlertTriangle, Lightbulb, FileText } from 'lucide-react';

interface ContentBlockRendererProps {
  block: ContentBlock;
}

const ContentBlockRenderer: React.FC<ContentBlockRendererProps> = ({ block }) => {
  switch (block.type) {
    case ContentBlockType.HEADING:
      return renderHeading(block);
    case ContentBlockType.PARAGRAPH:
      return renderParagraph(block);
    case ContentBlockType.CODE:
      return renderCode(block);
    case ContentBlockType.IMAGE:
      return renderImage(block);
    case ContentBlockType.CALLOUT:
      return renderCallout(block);
    case ContentBlockType.LIST:
      return renderList(block);
    case ContentBlockType.QUOTE:
      return renderQuote(block);
    case ContentBlockType.DIVIDER:
      return renderDivider();
    case ContentBlockType.SPACER:
      return renderSpacer(block);
    case ContentBlockType.TABLE:
      return renderTable(block);
    case ContentBlockType.VIDEO:
      return renderVideo(block);
    case ContentBlockType.LINK:
      return renderLink(block);
    default:
      return null;
  }
};

const renderHeading = (block: ContentBlock) => {
  const level = block.level || 2;
  const classNameMap: Record<number, string> = {
    1: 'text-5xl font-extrabold text-gray-900 dark:text-gray-100 mb-6 mt-14 tracking-tight font-display',
    2: 'text-4xl font-bold text-gray-900 dark:text-gray-100 mb-5 mt-12 tracking-tight font-display',
    3: 'text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4 mt-10 font-display',
    4: 'text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 font-display',
    5: 'text-xl font-semibold text-primary-700 dark:text-primary-400 mb-3 mt-6',
    6: 'text-base font-semibold text-gray-600 dark:text-gray-400 mb-2 mt-4 uppercase tracking-wide',
  };
  const className = classNameMap[level] || classNameMap[2];

  const HeadingTag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const headingText = String(block.content || '');
  const numberedHeading = headingText.match(/^(\d{1,2}|(?:(?:[0-9]\uFE0F?\u20E3)|🔟)+)[.)]?\s+(.+)$/u);
  const normalizedHeadingNumber = numberedHeading?.[1]
    .replace(/\uFE0F|\u20E3/g, '')
    .replace(/🔟/g, '10');
  const headingContent = numberedHeading ? (
    <>
      <span className="article-section-number article-number-badge" aria-hidden="true">
        {normalizedHeadingNumber}
      </span>
      <span>{numberedHeading[2]}</span>
    </>
  ) : block.content;
  const headingProps = numberedHeading
    ? { 'data-toc-title': `${normalizedHeadingNumber} ${numberedHeading[2]}` }
    : {};

  switch (HeadingTag) {
    case 'h1':
      return <h1 className={className} {...headingProps}>{headingContent}</h1>;
    case 'h2':
      return <h2 className={className} {...headingProps}>{headingContent}</h2>;
    case 'h3':
      return <h3 className={className} {...headingProps}>{headingContent}</h3>;
    case 'h4':
      return <h4 className={className} {...headingProps}>{headingContent}</h4>;
    case 'h5':
      return <h5 className={className} {...headingProps}>{headingContent}</h5>;
    case 'h6':
      return <h6 className={className} {...headingProps}>{headingContent}</h6>;
    default:
      return <h2 className={className} {...headingProps}>{headingContent}</h2>;
  }
};

const renderParagraph = (block: ContentBlock) => {
  const parts = String(block.content || '').split(/([✅❌❓])/u);
  return (
    <p className="text-base text-gray-700 dark:text-gray-300 mb-4 leading-7">
      {parts.map((part, index) => {
        if (part === '✅') return <CheckCircle key={index} className="article-inline-status is-positive" aria-label="Positive" />;
        if (part === '❌') return <AlertCircle key={index} className="article-inline-status is-negative" aria-label="Negative" />;
        if (part === '❓') return <CircleHelp key={index} className="article-inline-status is-question" aria-label="Question" />;
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </p>
  );
};

const renderCode = (block: ContentBlock) => {
  // Support multi-language snippets via metadata.snippets
  if (block.metadata?.snippets && Array.isArray(block.metadata.snippets)) {
    const snippets = block.metadata.snippets as { language: string; code: string }[];
    const defaultLang = snippets[0]?.language || 'typescript';
    return (
      <div className="my-8">
        <CodeBlock snippets={snippets} defaultLanguage={defaultLang} />
      </div>
    );
  }

  if (!block.codeConfig) return null;

  const { language, code, fileName, variants } = block.codeConfig;

  if (language === 'mermaid') {
    return <MermaidDiagram definition={code} />;
  }

  if (language === 'component-diagram') {
    return <ComponentDiagram definition={code} />;
  }

  // Build the snippets array: primary language + any variants
  const snippets = [{ language, code }];
  if (variants && variants.length > 0) {
    variants.forEach((v: { language: string; code: string }) => {
      snippets.push({ language: v.language, code: v.code });
    });
  }

  return (
    <div className="my-8">
      {fileName && (
        <div className="px-4 py-3 bg-gray-800 dark:bg-gray-900 text-gray-300 text-sm font-medium border-b border-gray-700 rounded-t-lg">
          📄 {fileName}
        </div>
      )}
      <CodeBlock snippets={snippets} defaultLanguage={language} />
    </div>
  );
};

const renderImage = (block: ContentBlock) => {
  if (!block.imageConfig) return null;

  const { url, alt, caption, fullWidth } = block.imageConfig;

  return (
    <figure className={`my-6 ${fullWidth ? 'w-full' : 'max-w-2xl mx-auto'}`}>
      <img
        src={url}
        alt={alt || caption || ''}
        className={`rounded-lg ${fullWidth ? 'w-full' : 'w-full'}`}
      />
      {caption && (
        <figcaption className="mt-2 text-sm text-center text-gray-600 dark:text-gray-400 italic">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

const renderCallout = (block: ContentBlock) => {
  if (!block.calloutVariant || !block.content) return null;

  const variantStyles = {
    [CalloutVariant.INFO]: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: Info,
      iconColor: 'text-blue-600 dark:text-blue-400',
      text: 'text-blue-800 dark:text-blue-200',
    },
    [CalloutVariant.WARNING]: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: AlertTriangle,
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      text: 'text-yellow-800 dark:text-yellow-200',
    },
    [CalloutVariant.ERROR]: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: AlertCircle,
      iconColor: 'text-red-600 dark:text-red-400',
      text: 'text-red-800 dark:text-red-200',
    },
    [CalloutVariant.SUCCESS]: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      icon: CheckCircle,
      iconColor: 'text-green-600 dark:text-green-400',
      text: 'text-green-800 dark:text-green-200',
    },
    [CalloutVariant.TIP]: {
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-800',
      icon: Lightbulb,
      iconColor: 'text-purple-600 dark:text-purple-400',
      text: 'text-purple-800 dark:text-purple-200',
    },
    [CalloutVariant.NOTE]: {
      bg: 'bg-gray-50 dark:bg-gray-800',
      border: 'border-gray-200 dark:border-gray-700',
      icon: FileText,
      iconColor: 'text-gray-600 dark:text-gray-400',
      text: 'text-gray-800 dark:text-gray-200',
    },
  };

  const styles = variantStyles[block.calloutVariant];
  const Icon = styles.icon;

  return (
    <div className={`my-6 p-4 rounded-lg border ${styles.bg} ${styles.border}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${styles.iconColor} flex-shrink-0 mt-0.5`} />
        <div className={`flex-1 ${styles.text}`}>
          {block.content}
        </div>
      </div>
    </div>
  );
};

const renderList = (block: ContentBlock) => {
  if (!block.listItems || block.listItems.length === 0) return null;

  const ListTag = block.listType === ListType.ORDERED ? 'ol' : 'ul';
  const className = block.listType === ListType.ORDERED
    ? 'list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-5 ml-4 text-base leading-7'
    : 'space-y-2 text-gray-700 dark:text-gray-300 mb-5 ml-4 text-base leading-7';

  if (block.listType === ListType.ORDERED) {
    return (
      <ol className={className}>
        {block.listItems.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ol>
    );
  }

  return (
    <ul className={className}>
      {block.listItems.map((item, index) => {
        const status = item.match(/^([✅❌])\s*(.*)$/u);
        const StatusIcon = status?.[1] === '✅' ? CheckCircle : status?.[1] === '❌' ? AlertCircle : null;

        return (
          <li key={index} className={`article-list-item flex items-start gap-2 ${StatusIcon ? 'article-list-item--status' : ''}`}>
            {StatusIcon ? (
              <StatusIcon
                className={`article-list-marker article-list-marker--${status?.[1] === '✅' ? 'positive' : 'negative'} w-4 h-4 flex-shrink-0 mt-1`}
                aria-hidden="true"
              />
            ) : (
              <span className="article-list-marker article-list-marker--bullet mt-2" aria-hidden="true" />
            )}
            <span>{status ? status[2] : item}</span>
          </li>
        );
      })}
    </ul>
  );
};

const renderQuote = (block: ContentBlock) => {
  if (!block.content) return null;

  return (
    <blockquote className="my-5 px-5 py-4 border-l-4 border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-r-lg text-gray-800 dark:text-gray-200 font-medium">
      {block.content}
    </blockquote>
  );
};

const renderDivider = () => {
  return (
    <div className="my-8 border-t border-gray-200 dark:border-gray-700"></div>
  );
};

const renderSpacer = (block: ContentBlock) => {
  // Use metadata to store spacer height, default to medium
  const height = block.metadata?.height || 'medium';
  const heightMap: Record<string, string> = {
    small: 'h-4',
    medium: 'h-8',
    large: 'h-12',
    xlarge: 'h-16',
  };
  const heightClass = heightMap[height] || heightMap.medium;

  return (
    <div className={`w-full ${heightClass}`} aria-hidden="true"></div>
  );
};

const renderTable = (block: ContentBlock) => {
  if (!block.tableConfig) return null;

  const { headers, rows, striped } = block.tableConfig;

  return (
    <div className="my-6 overflow-x-auto">
      <table className={`min-w-full border-collapse border border-gray-300 dark:border-gray-700 ${striped ? 'table-striped' : ''}`}>
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-800">
            {headers.map((header, index) => (
              <th
                key={index}
                className="px-4 py-2 text-left border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 font-semibold"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={striped && rowIndex % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : ''}
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderVideo = (block: ContentBlock) => {
  if (!block.videoUrl) return null;

  // Extract video ID from YouTube/Vimeo URLs
  const getEmbedUrl = (url: string): string | null => {
    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoRegex = /vimeo\.com\/(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return null;
  };

  const embedUrl = getEmbedUrl(block.videoUrl);
  if (!embedUrl) {
    return (
      <div className="my-6">
        <a href={block.videoUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
          {block.videoUrl}
        </a>
      </div>
    );
  }

  return (
    <div className="my-6 aspect-video w-full">
      <iframe
        src={embedUrl}
        title={`Embedded video: ${block.videoUrl}`}
        className="w-full h-full rounded-lg"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

const renderLink = (block: ContentBlock) => {
  if (!block.linkUrl) return null;

  return (
    <div className="my-4">
      <a
        href={block.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
      >
        {block.linkText || block.linkUrl}
      </a>
    </div>
  );
};

export default ContentBlockRenderer;
